import {
  AlertTriangle,
  BadgeDollarSignIcon,
  BadgeInfoIcon,
  CalendarCheckIcon,
} from 'lucide-react'
import React, { type ReactNode, useEffect, useState } from 'react'

import { membersDocuments } from 'rails_routes'
import { Badge, SolidCircleCheckIcon, TextLink } from 'slab'
import { type UserFacingEnrollmentMetadata } from 'types/member_onboarding_v2'
import { conditionallyLocalizeDate, formatList, formatMoney } from 'utils'

import {
  emitPostEnrollmentPageExitEvent,
  emitPostEnrollmentPageViewEvent,
} from './events'
import { type QmeTreatment } from './types'
import { ALERA_MEDICARE_FORM_URL, MEDICARE_FORM_URL } from './utils'
import { type MoneyAmount } from '../../types'

const Card = ({
  icon,
  title,
  subtitle,
  deadline,
}: {
  icon?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  deadline?: Date;
}) => {
  return (
    <div className='flex items-start gap-2 p-4 border border-gray-200 rounded-lg'>
      {icon && <div className='w-4 h-4'>{icon}</div>}
      <div className='flex flex-col items-start gap-3'>
        {deadline && (
          <Badge type='warning' leftIcon={AlertTriangle}>
            Action needed by {conditionallyLocalizeDate(deadline)}
          </Badge>
        )}
        <div className='flex flex-col items-start gap-2'>
          {subtitle
            ? (
              <h3 className='body-sm-bold'>{title}</h3>
              )
            : (
              <div className='body-sm'>{title}</div>
              )}
          {subtitle && <div className='body-sm text-gray-500'>{subtitle}</div>}
        </div>
      </div>
    </div>
  )
}

type TimeSpecificTaskMetadata = {
  deadline: Date;
  description: React.ReactNode;
}

type Props = {
  enrollments?: Array<UserFacingEnrollmentMetadata>;
  coverageStartDate: Date;
  companyName?: string;
  // be careful with the payByBank field!
  // payByBank is true if the user selected that they would pay by bank for their off-Thatch
  // medical plan. if they chose BYOP, this would be false if they chose to pay by card (or reimbursements).
  // if they did not choose BYOP, this would be false but it wouldn't have any semantic meaning.
  payByBank?: boolean;
  allowanceAmount?: MoneyAmount;
  totalPremiumsAmount?: MoneyAmount;
  qmeTreatment: QmeTreatment;
  userId?: string;
  delayCard?: boolean;
  enrollmentsSubmittedAt: Date;
  useAleraMedicareForm?: boolean;
}

// long ago the four enrollment taxonomy types lived in harmony
type EnrollmentTaxonomy = 'medicare' | 'on_thatch' | 'off_thatch' | 'self_pay'

export const getPaymentType = (
  enrollment: UserFacingEnrollmentMetadata
): EnrollmentTaxonomy => {
  if (enrollment.is_medicare) {
    return 'medicare'
  } else if (enrollment.is_reimbursement_only) {
    return 'self_pay'
  } else if (enrollment.is_off_thatch) {
    return 'off_thatch'
  } else {
    return 'on_thatch'
  }
}

export default function PostEnrollmentActions (props: Props) {
  const {
    enrollments,
    coverageStartDate,
    companyName,
    payByBank,
    qmeTreatment,
    userId,
    delayCard,
    enrollmentsSubmittedAt,
    useAleraMedicareForm,
  } = props
  const allowanceValue = Number(props.allowanceAmount?.value) || 0
  const totalPremiumsValue = Number(props.totalPremiumsAmount?.value) || 0

  const coverageStartStr = conditionallyLocalizeDate(coverageStartDate)

  const onThatchEnrollments = enrollments
    ? enrollments.filter((e) => getPaymentType(e) === 'on_thatch')
    : []
  const onThatchCarriers = onThatchEnrollments.map((e) => e.carrier_name)
  const offThatchEnrollments = enrollments
    ? enrollments.filter((e) => getPaymentType(e) === 'off_thatch')
    : []
  const offThatchEnrollmentsCarrierNames = offThatchEnrollments
    .map((e) => e.carrier_name)
    .filter((cn) => cn.length > 0) // may not be present
  const offThatchFallback =
    offThatchEnrollments.length > 1
      ? 'your self-enrolled plan carriers'
      : 'your insurance company'
  const offThatchDisplay =
    formatList(offThatchEnrollmentsCarrierNames).length > 0
      ? formatList(offThatchEnrollmentsCarrierNames)
      : offThatchFallback

  const medicareEnrollments = enrollments
    ? enrollments.filter((e) => getPaymentType(e) === 'medicare')
    : []
  const selfPayEnrollments = enrollments
    ? enrollments.filter((e) => getPaymentType(e) === 'self_pay')
    : []
  const selfEnrolledCarrierNames = selfPayEnrollments
    .map((e) => e.carrier_name)
    .filter((cn) => cn.length > 0) // may not be present
  const selfEnrolledFallback =
    selfPayEnrollments.length > 1
      ? 'your self-enrolled plan carriers'
      : 'your insurance company'
  const selfEnrolledDisplay =
    formatList(selfEnrolledCarrierNames).length > 0
      ? formatList(selfEnrolledCarrierNames)
      : selfEnrolledFallback

  const hasLeftOverFunds = !!(
    totalPremiumsValue &&
    allowanceValue &&
    totalPremiumsValue < allowanceValue
  )
  const showQMENotice =
    qmeTreatment !== 'disabled' &&
    !!allowanceValue &&
    !!totalPremiumsValue &&
    allowanceValue > totalPremiumsValue
  const showDeduction =
    !!allowanceValue &&
    !!totalPremiumsValue &&
    allowanceValue < totalPremiumsValue

  // Open enrollment events will have coverage start the next year
  const isOpenEnrollment = coverageStartDate.getFullYear() > enrollmentsSubmittedAt.getFullYear()

  const calculateTimeAfterEnrollmentsSubmitted = (
    numDaysAfter: number
  ): Date => {
    const dateAfter = new Date(enrollmentsSubmittedAt)
    dateAfter.setDate(dateAfter.getDate() + numDaysAfter)
    return dateAfter
  }

  const calculateTimeBeforeCoverage = (numDaysBefore: number): Date => {
    const dateBefore = new Date(coverageStartDate)
    dateBefore.setDate(dateBefore.getDate() - numDaysBefore)
    return dateBefore
  }

  const getEarliestDate = (...dates: Date[]): Date => {
    return dates.reduce((earliest, current) => {
      return earliest.getTime() < current.getTime() ? earliest : current
    }, dates[0])
  }

  const getTimeSpecificTasks = (): Array<TimeSpecificTaskMetadata> => {
    const timeSpecificTasks: Array<TimeSpecificTaskMetadata> = []
    const allEnrollments = props.enrollments || []

    allEnrollments.forEach((enrollment: UserFacingEnrollmentMetadata) => {
      const {
        requires_autopay_enrollment: requiresAutopayEnrollment,
        requires_binder_payment: requiresBinderPayment,
        carrier_name: carrierName,
        requires_docusign: requiresDocusign,
        requires_proof_of_residency: requiresProofOfResidency,
      } = enrollment

      if (requiresBinderPayment) {
        const deadline = isOpenEnrollment
          ? getEarliestDate(
            calculateTimeAfterEnrollmentsSubmitted(7),
            new Date(enrollmentsSubmittedAt.getFullYear(), 11, 8)) // Dec 8
          : calculateTimeBeforeCoverage(10)

        timeSpecificTasks.push({
          deadline,
          description: (
            <Card
              deadline={deadline}
              title={`Pay for your first month's coverage with ${carrierName}`}
              subtitle={
                <>
                  Provide the bank account details on your Thatch dashboard to{' '}
                  {carrierName} so Thatch can pay on your behalf. This is
                  required to complete your enrollment with {carrierName}, and{' '}
                  <strong>
                    your failure to complete this task in time can cause a lapse
                    in coverage
                  </strong>
                  .
                </>
              }
            />
          ),
        })
      }

      if (medicareEnrollments.length) {
        const deadline = isOpenEnrollment
          ? getEarliestDate(
            calculateTimeAfterEnrollmentsSubmitted(7),
            new Date(enrollmentsSubmittedAt.getFullYear(), 11, 15)) // Dec 15
          : calculateTimeBeforeCoverage(7)
        const medicareFormUrl = useAleraMedicareForm
          ? ALERA_MEDICARE_FORM_URL
          : MEDICARE_FORM_URL
        timeSpecificTasks.push({
          deadline,
          description: (
            <Card
              deadline={deadline}
              title="Submit your Medicare enrollment form, if you haven't already"
              subtitle={
                <span className='[&_a]:inline'>
                  You can find the form{' '}
                  <TextLink href={medicareFormUrl}>
                    here
                  </TextLink>
                </span>
              }
            />
          ),
        })
      }

      if (requiresDocusign) {
        const deadline = isOpenEnrollment
          ? getEarliestDate(
            calculateTimeAfterEnrollmentsSubmitted(10),
            new Date(enrollmentsSubmittedAt.getFullYear(), 11, 10)) // Dec 10
          : calculateTimeBeforeCoverage(7)
        timeSpecificTasks.push({
          deadline,
          description: (
            <Card
              deadline={deadline}
              title={
                <>
                  Sign the DocuSign sent to you by{' '}
                  <TextLink href='mailto:broker@thatch.com'>
                    broker@thatch.com
                  </TextLink>
                </>
              }
              subtitle={
                <>
                  This is required to complete your enrollment with{' '}
                  {carrierName}, and{' '}
                  <strong>
                    failure to sign the form in time can cause a lapse in
                    coverage
                  </strong>
                </>
              }
            />
          ),
        })
      }

      if (requiresProofOfResidency) {
        const deadline = isOpenEnrollment
          ? getEarliestDate(
            calculateTimeAfterEnrollmentsSubmitted(10),
            new Date(enrollmentsSubmittedAt.getFullYear(), 11, 10)) // Dec 10
          : calculateTimeBeforeCoverage(7)
        timeSpecificTasks.push({
          deadline,
          description: (
            <Card
              deadline={deadline}
              title={
                <>
                  Upload two proofs of residency to the{' '}
                  <TextLink href={membersDocuments.index()}>Documents</TextLink>{' '}
                  tab on your Thatch dashboard
                </>
              }
              subtitle={
                <>
                  {carrierName} needs two documents that prove your residential
                  address (like an ID card or utility bill) to complete your
                  enrollment.{' '}
                  <strong>
                    Failure to upload these in time can cause a lapse in
                    coverage
                  </strong>
                </>
              }
            />
          ),
        })
      }

      if (requiresAutopayEnrollment) {
        const deadline = isOpenEnrollment
          ? new Date(enrollmentsSubmittedAt.getFullYear(), 11, 10) // Dec 10
          : calculateTimeBeforeCoverage(0)
        timeSpecificTasks.push({
          deadline,
          description: (
            <Card
              deadline={deadline}
              title={`Set up autopay with ${carrierName} using the bank account details on your Thatch dashboard`}
              subtitle={
                <>
                  Thatch will make the payments on your behalf, but{' '}
                  <strong>
                    {carrierName} requires that you set up the autopay details
                    with them
                  </strong>
                  . Failure to add this information in time can cause a lapse in
                  coverage.
                </>
              }
            />
          ),
        })
      }
    })

    return timeSpecificTasks
  }

  const getMemberCards = (): React.ReactNode[] => {
    const cards: React.ReactNode[] = []
    const timeSpecificTasks = getTimeSpecificTasks()
    timeSpecificTasks.sort(
      (a, b) => a.deadline.getTime() - b.deadline.getTime()
    )

    timeSpecificTasks.forEach((task) => {
      cards.push(task.description)
    })

    const allEnrollments = props.enrollments || []
    const onThatchAndSendsEmail = allEnrollments.filter(
      (e) => getPaymentType(e) === 'on_thatch' && e.carrier_sends_payment_emails
    )

    if (medicareEnrollments.length) {
      cards.push(
        <Card
          title='Set up autopay with Medicare'
          subtitle={
            <>
              You are responsible for paying your Medicare premiums upfront.
              Since Medicare bills quarterly, you can request a reimbursement up
              to your monthly budget amount, even if it's greater than the
              monthly cost of your plan premium.
            </>
          }
        />
      )
    }

    if (showQMENotice && !delayCard) {
      cards.push(
        <Card
          title='Request a Thatch card to use your Thatch balance'
          subtitle={
            <span className='[&_a]:inline'>
              You'll accumulate{' '}
              {formatMoney(allowanceValue - totalPremiumsValue)} each month to
              spend on{' '}
              <TextLink
                target='_blank'
                href='https://support.thatch.com/qualified-medical-expense'
              >
                healthcare expenses
              </TextLink>{' '}
              like copays, prescriptions, and more
            </span>
          }
        />
      )
    }

    if (selfPayEnrollments.length) {
      cards.push(
        <Card
          deadline={coverageStartDate}
          title={`Set up autopay with ${selfEnrolledDisplay}`}
          subtitle={`Since you enrolled in ${
            selfPayEnrollments.length > 1 ? 'plans' : 'a plan'
          } outside Thatch, you are responsible for making sure ${
            selfPayEnrollments.length > 1 ? 'they stay' : 'it stays'
          } in good financial standing`}
        />
      )
      cards.push(
        <Card
          title='Manage your self-enrolled plan'
          subtitle={`Check directly with ${selfEnrolledDisplay} on when your member ID ${
            selfPayEnrollments.length > 1 ? 'cards' : 'card'
          } will arrive`}
        />
      )
    }

    if (onThatchCarriers.length) {
      const reminderText =
        onThatchAndSendsEmail.length > 0
          ? `Ignore payment reminder emails from ${formatList(
              onThatchAndSendsEmail.map((e) => e.carrier_name)
            )}`
          : `Remember ${
              onThatchCarriers.length > 1
                ? `these ${formatList(onThatchCarriers)} plans`
                : `this ${onThatchCarriers} plan`
            } is yours!`

      cards.push(
        <Card
          title={reminderText}
          subtitle={
            <div className='space-y-2'>
              <p>
                You're the owner of your{' '}
                {onThatchCarriers.length > 1 ? 'plans' : 'plan'}, but{' '}
                <span className='body-sm-bold'>
                  Thatch will pay the premiums
                </span>{' '}
                as long as you're employed by {companyName || 'your company'}
              </p>
              <p>
                If you leave {companyName || 'your company'}, you'll either need
                to cancel your {onThatchCarriers.length > 1 ? 'plans' : 'plan'}{' '}
                or take over the responsibility of paying for{' '}
                {onThatchCarriers.length > 1 ? 'them' : 'it'}
              </p>
            </div>
          }
        />
      )
    }

    if (offThatchEnrollments.length) {
      cards.push(
        <Card
          title={`Set up autopay with ${offThatchDisplay} using the ${
            payByBank ? 'bank account' : 'card'
          } details on your Thatch dashboard`}
          subtitle={`Since you enrolled in ${
            offThatchEnrollments.length > 1 ? 'plans' : 'a plan'
          } outside Thatch, you are responsible for making sure ${
            offThatchEnrollments.length > 1 ? 'they stay' : 'it stays'
          } in good financial standing`}
        />
      )
      cards.push(
        <Card
          title='Manage your self-enrolled plan'
          subtitle={`Check directly with ${offThatchDisplay} on when your member ID ${
            offThatchEnrollments.length > 1 ? 'cards' : 'card'
          } will arrive`}
        />
      )
    }

    if (onThatchEnrollments?.length) {
      cards.push(
        <Card
          title={`Look out for your ${formatList(onThatchCarriers)} insurance ${
            onThatchCarriers.length > 1 ? 'cards' : 'card'
          }`}
          subtitle={`Expect to receive them 2-4 weeks after ${coverageStartStr}`}
        />
      )
    }

    if (selfPayEnrollments.length || medicareEnrollments.length) {
      const monthlyTasks: string[] = []
      const selfEnrollPremiumTotals = selfPayEnrollments.reduce(
        (sum, e) => sum + Number(e.premium_amount?.value || 0),
        0
      )

      if (medicareEnrollments.length) {
        monthlyTasks.push(
          '• Request reimbursement for your Medicare plan premiums'
        )
      }

      if (hasLeftOverFunds && selfPayEnrollments.length) {
        monthlyTasks.push(
          `• Request reimbursement for your ${formatMoney(
            selfEnrollPremiumTotals
          )} ${
            selfPayEnrollments.length > 1 ? 'premiums' : 'premium'
          } with ${selfEnrolledDisplay}`
        )
      }

      if (hasLeftOverFunds && qmeTreatment === 'all_allowed') {
        monthlyTasks.push(
          `• Request reimbursement for additional healthcare expenses up to your ${formatMoney(
            allowanceValue - totalPremiumsValue
          )} / month Thatch balance`
        )
      }

      if (hasLeftOverFunds && qmeTreatment === 'copay_only') {
        monthlyTasks.push(
          `• Request reimbursement for your copays up to your ${formatMoney(
            allowanceValue - totalPremiumsValue
          )} / month Thatch balance`
        )
      }

      if (
        !medicareEnrollments.length &&
        totalPremiumsValue &&
        allowanceValue &&
        totalPremiumsValue >= allowanceValue
      ) {
        monthlyTasks.push(
          `• Request reimbursement for ${
            companyName ? companyName + "'s" : "your employer's"
          } ${formatMoney(allowanceValue)} contribution to your ${formatMoney(
            totalPremiumsValue
          )} premiums`
        )
      }

      if (monthlyTasks.length > 0) {
        cards.push(
          <Card
            title={`Monthly, starting ${coverageStartStr}:`}
            subtitle={
              <div className='space-y-1'>
                {monthlyTasks.map((task, index) => (
                  <div key={index}>{task}</div>
                ))}
              </div>
            }
          />
        )
      }
    }

    return cards
  }

  const getThatchCards = (): React.ReactNode[] => {
    const cards: React.ReactNode[] = []

    if (onThatchEnrollments?.length) {
      cards.push(
        <Card
          icon={<SolidCircleCheckIcon width={16} height={16} color='green' />}
          title={`Submitting your enrollments to ${formatList(
            onThatchCarriers
          )} for ${coverageStartStr} coverage`}
        />
      )
    }

    if (offThatchEnrollments.length) {
      cards.push(
        <Card
          icon={<BadgeDollarSignIcon width={16} height={16} />}
          title={`Creating ${
            payByBank ? 'a bank account' : 'card information'
          } for your use on plan premiums`}
        />
      )
    }

    if (medicareEnrollments.length) {
      cards.push(
        <Card
          icon={<BadgeInfoIcon width={16} height={16} />}
          title='Updating your Thatch dashboard with the Medicare information you provided in the enrollment form'
        />
      )
    }

    const monthlyTasks: string[] = []

    if (onThatchEnrollments?.length) {
      monthlyTasks.push(
        `• Paying ${formatList(
          onThatchEnrollments.map((e) => e.carrier_name)
        )} for your premiums`
      )
    }

    let fundingSource = null
    if (offThatchEnrollments.length > 0) {
      fundingSource = payByBank ? 'bank account' : 'card'
    }
    if (selfPayEnrollments.length > 0 || medicareEnrollments.length > 0) {
      fundingSource = 'Thatch balance'
    }

    if (fundingSource) {
      const offThatchPremiumTotal = offThatchEnrollments.reduce(
        (sum, e) => sum + Number(e.premium_amount?.value || 0),
        0
      )
      const selfPayPremiumTotal = selfPayEnrollments.reduce(
        (sum, e) => sum + Number(e.premium_amount?.value || 0),
        0
      )
      const medicarePremiumTotal = medicareEnrollments.reduce(
        (sum, e) => sum + Number(e.premium_amount?.value || 0),
        0
      )
      const totalNonOnThatchPremium =
        offThatchPremiumTotal + selfPayPremiumTotal + medicarePremiumTotal
      if (hasLeftOverFunds && showQMENotice && totalNonOnThatchPremium > 0) {
        monthlyTasks.push(
          `• Funding your ${fundingSource} with ${formatMoney(
            totalNonOnThatchPremium
          )} to cover your premium payments, plus ${formatMoney(
            allowanceValue - totalPremiumsValue
          )}
          available for healthcare expenses`
        )
      } else if (hasLeftOverFunds) {
        if (totalNonOnThatchPremium > 0) {
          monthlyTasks.push(
            `• Funding your ${fundingSource} with ${formatMoney(
              totalNonOnThatchPremium
            )} to cover your premium payments`
          )
        }
      } else {
        const nonThatchPremiumIsLessThanAllowance =
          totalNonOnThatchPremium < allowanceValue
        const amount = nonThatchPremiumIsLessThanAllowance
          ? totalNonOnThatchPremium
          : allowanceValue
        const formattedAmount = formatMoney(amount)
        if (totalNonOnThatchPremium > 0) {
          if (nonThatchPremiumIsLessThanAllowance) {
            monthlyTasks.push(
              `• Funding your ${fundingSource} with ${formattedAmount} to cover your premium payments`
            )
          } else {
            monthlyTasks.push(
              `• Funding your ${fundingSource} with up to ${formattedAmount} to cover your premium payments`
            )
          }
        }
      }
    }

    // This message will be shown for both on-thatch and off-thatch cases
    if (showDeduction) {
      monthlyTasks.push(
        `• Deducting ${formatMoney(
          totalPremiumsValue - allowanceValue
        )} from your paycheck for the month to cover your total premiums of ${formatMoney(
          totalPremiumsValue
        )}`
      )
    }

    // This message will be shown when all plans are on-thatch and the user has QME enabled.
    if (!fundingSource && showQMENotice) {
      monthlyTasks.push(
        `• Adding ${formatMoney(
          allowanceValue - totalPremiumsValue
        )} to your Thatch balance`
      )
    }

    if (monthlyTasks.length > 0) {
      cards.push(
        <Card
          icon={<CalendarCheckIcon width={16} height={16} color='gray' />}
          title={
            <>
              <span className='body-sm'>
                Monthly, starting {coverageStartStr}:
              </span>
              <ul className='body-sm mt-2 space-y-2'>
                {monthlyTasks.map((task, index) => (
                  <li key={index}>{task}</li>
                ))}
              </ul>
            </>
          }
        />
      )
    }

    return cards
  }

  const memberCards = getMemberCards()
  const thatchCards = getThatchCards()

  const [startTime, setStartTime] = useState<number>(Date.now())

  useEffect(() => {
    setStartTime(Date.now())

    emitPostEnrollmentPageViewEvent(userId, {
      employerName: companyName,
      coverageStartDate: conditionallyLocalizeDate(coverageStartDate),
      allowanceAmount: props.allowanceAmount?.value,
      totalPremiumsAmount: props.totalPremiumsAmount?.value,
      enrollmentCount: enrollments?.length || 0,
      onThatchEnrollmentCount: onThatchEnrollments.length,
      offThatchEnrollmentCount: offThatchEnrollments.length,
      selfPayEnrollmentCount: selfPayEnrollments.length,
      medicareEnrollmentCount: medicareEnrollments.length,
      qmeTreatment,
      payByBank,
      hasLeftOverFunds,
      needsPayrollDeduction: showDeduction,
    })

    const handleBeforeUnload = () => {
      emitPostEnrollmentPageExitEvent(
        Math.round((Date.now() - startTime) / 1000),
        userId
      )
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  return (
    <div className='space-y-8'>
      {memberCards.length > 0 && (
        <div className='space-y-4'>
          <h2 className='heading-sm'>On your plate:</h2>
          <div className='space-y-2'>
            {memberCards.map((card, index) => (
              <div key={index}>{card}</div>
            ))}
          </div>
        </div>
      )}

      {thatchCards.length > 0 && (
        <div className='space-y-4'>
          <h2 className='heading-sm'>We'll take care of:</h2>
          <div className='space-y-2'>
            {thatchCards.map((card, index) => (
              <div key={index}>{card}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
